import React, {CSSProperties, useCallback, useMemo, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {OrderStatus, PaymentMethod} from 'grpc-bob/enum_pb';
import {Order} from 'grpc-bob/subscription_pb';

import PaymentLayout from '../../layouts/PaymentLayout';
import MethodForPayment from '../../components/MethodForPayment';
import StepRenderer from '../../components/StepRenderer';
import Description from '../../components/Description';
import TableDetail from '../../components/TableDetail';
import CodCooldown from '../../components/CodCooldown';
import Instruction from '../../components/Instruction';
import DetailItem from '../../components/DetailItem';
import {ButtonProps} from '../../components/Button';
import Paper from '../../components/Paper';

import {useEffectOnce} from 'react-use';
import {useTranslation} from 'react-i18next';
import {useDispatchCtx} from '../../context';

import StudentService from '../../services/student';
import SubscriptionService from '../../services/subscription';
import {AppState, setOrderWaitingPayment, setSummary} from '../../state-management/app';
import getKnowNative, {MIXPANEL} from '../../externals/mobile-communication';

import {
  ClientStorage,
  PaymentStorageType,
  conditionToStep,
  getDateProperty,
  getMsgFromErr,
  parseQueryToField,
  toVND,
  delay,
  isOrderStillProcessing,
} from '../../utils';
import {PAYMENT_INSTRUCTIONS} from '../../constants/render';
import {PaymentMethodKeyReverse} from '../../constants/map';

import styles from './Summary.module.css';


interface PaymentRedirectProps extends RouteComponentProps<{ status: string }>, AppState {
}

type MessageChooser = {
  [x in OrderStatus]: {
    message: string,
    style: CSSProperties
  }
}

const messageChooser: MessageChooser = {
  [OrderStatus.ORDER_STATUS_SUCCESSFULLY]: {
    message: 'paymentSuccessful',
    style: {
      color: '#3ACE85'
    }
  },
  [OrderStatus.ORDER_STATUS_FAILED]: {
    message: 'paymentFailed',
    style: {
      color: 'red'
    }
  },
  [OrderStatus.ORDER_STATUS_CANCELED]: {
    message: 'paymentCancelled',
    style: {
      color: 'red'
    }
  },
  [OrderStatus.ORDER_STATUS_WAITING_FOR_PAYMENT]: {
    message: 'paymentPending',
    style: {
      color: 'orange'
    }
  },
  [OrderStatus.ORDER_STATUS_NONE]: {
    message: 'paymentNone',
    style: {
      color: 'orange' //do not know what color to use
    }
  },
  [OrderStatus.ORDER_STATUS_ENDED]: {
    message: 'paymentEnded',
    style: {
      color: 'orange' //do not know what color to use
    }
  },
  [OrderStatus.ORDER_STATUS_PROCESSING_PAYMENT]: {
    message: 'paymentProcessing',
    style: {
      color: 'orange'
    }
  },
  [OrderStatus.ORDER_STATUS_DELETED]: {
    message: 'paymentDeleted',
    style: {
      color: 'red'
    }
  }
};


const Summary = (props: PaymentRedirectProps) => {
  const [importantMessage, setImportantMessage] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [orderProcessing, setOrderProcessing] = useState<PaymentStorageType | undefined>();
  const {t} = useTranslation();
  const dispatch = useDispatchCtx();

  const {orderSummary, user, history, match: {params: {status}}} = props;

  const isOrderStillValid =  (() => {
    if (orderSummary) return isOrderStillProcessing(orderSummary.status);
    if (!Number.isNaN(Number(status))) return isOrderStillProcessing(Number(status) as OrderStatus);

    return true; //we dont want to show retry in case we dont have enough information
  })();


  useEffectOnce(() => {
    //for mobile routing
    getKnowNative('summary');
    const {
      Ref: orderIdInUrl,
      token: overrideToken,
      isGateway: isRedirectedFromGateway
    } = parseQueryToField(props.location.search, ['Ref', 'token', 'isGateway']);

    const token = overrideToken || ClientStorage.token || props.token;

    if (!token || !orderIdInUrl) {
      const typeMessage = messageChooser[status as unknown as OrderStatus] || messageChooser[0]; //use from params

      setImportantMessage(
        <div>{`${t('statusOfPayment')}`}
          <span style={typeMessage.style}>{t(typeMessage.message)}</span>
          {`${t('insufficientData')}`}
        </div>
      );
      return setIsLoading(false);
    }

    void async function fetchSummary() {
      const orderProcessing = ClientStorage.orderProcessing;
      setOrderProcessing(orderProcessing);

      try { // mark order processing, dont care about error
        if (isRedirectedFromGateway) {
          await SubscriptionService.markOrderProcessing(orderIdInUrl!, token as string);
        }
      } catch (e) {
      }

      try {
        const [orders, student] = await Promise.all([ //please duplicate listOrder
          SubscriptionService.retrieveListOrder(token!),
          StudentService.getStudentByToken(token!)
        ]);
        //Because, mobile send Ref=[orderId], should be check type orderId. orderId is int compare with order.id
        let currentOrder: Order.AsObject | undefined;

        if (Number(orderIdInUrl)) {
          currentOrder = orders!.find((order) => order.id === Number(orderIdInUrl))
        } else {
          currentOrder = orders!.find((order) => order.referenceNumber === orderIdInUrl);
        }

        if (!student || !student.profile) {
          setImportantMessage(t('cannotFindThisUser'));
          return;
        }

        if (!currentOrder) {
          setImportantMessage(t('cannotFindOrder'));
          return;
        }
        dispatch(setSummary(currentOrder, student.profile));

        const shouldRedirectToMethodSelect =
          !isRedirectedFromGateway &&
          currentOrder &&
          currentOrder.status === OrderStatus.ORDER_STATUS_WAITING_FOR_PAYMENT;

        if (shouldRedirectToMethodSelect) {
          dispatch(setOrderWaitingPayment(currentOrder));
          return history.push(`/method?packageId=${currentOrder.packageId}&planId=${currentOrder.planId}&Ref=${currentOrder.id}`);
        }

        //for mix panel
        if (isRedirectedFromGateway) { //if token not exist, then this page is redirected from payment gateway or cod => send mixpanel, else from payment history from app=> do nothing
          getKnowNative('mixpanel', undefined, {
            type: MIXPANEL.PAYMENT_SUMMARY_CREATED,
            paymentStatus: (messageChooser[currentOrder.status] || {}).message,
            packageId: currentOrder.packageId,
            packageName: currentOrder.packageName,
            planId: currentOrder.planId,
            planName: currentOrder.planName,
            totalAmount: currentOrder.amount - currentOrder.carriedOverAmount - currentOrder.couponAmount,
            subscriptionsList: currentOrder.subscriptionsList || [],
            promotionCodeApplied: !!currentOrder.coupon,
            promotionCode: currentOrder.coupon,
            paymentOption: currentOrder.paymentMethod
          });
        }

      } catch (e) {
        setImportantMessage(t(getMsgFromErr(e)));
      } finally {
        setIsLoading(false)
      }
    }()
  });

  const onClickBackToApp = useCallback(() => {

    ClientStorage.clearAll();
    getKnowNative('end');

    delay(500).then(() => { //delay 500ms, if mobile didnt have a response, then it will redirect
      history.push(`/${orderSummary && !isOrderStillValid ? `package?planId=${orderSummary.planId}` : ''}`);
    })
  }, [history, orderSummary, isOrderStillValid]);

  const buttonChildren = isOrderStillValid ? t('backToApp') : t('retry');
  const buttonProps: ButtonProps = useMemo(() => ({
    disabled: isLoading,
    onClick: onClickBackToApp,
    children: buttonChildren
  }), [onClickBackToApp, isLoading, buttonChildren]);

  return (
    <PaymentLayout
      isLoading={isLoading}
      buttonProps={buttonProps}
    >
      <StepRenderer step={conditionToStep(importantMessage)}>
        <div className={styles.importantMessage}>
          {importantMessage}
        </div>
        <Paper>
          <div className={styles.root}>
            {
              orderSummary && user &&
              <>
                <h3 className="text is-title">{t('paymentSummary')}</h3>
                <Description className="m-b-1" title={t('referenceNumber')}>
                  {orderSummary.referenceNumber}
                </Description>
                <Description className="m-b-1" title={t('paymentStatus')}>
                  {
                    orderSummary.status === OrderStatus.ORDER_STATUS_PROCESSING_PAYMENT &&
                    orderSummary.codOrderInfo ?
                      <div>
                        {
                          orderSummary.codOrderInfo.expectedDeliveryTime &&
                          <CodCooldown
                            className={styles.cooldown}
                            paymentStatus={orderSummary.codOrderInfo.status}
                            expectedTime={orderSummary.codOrderInfo.expectedDeliveryTime.seconds}
                          />
                        }
                        <div>
                          <span className={styles.howToPay}>{t('howToPay')}</span>
                          <Instruction
                            className={styles.instruction}
                            instructions={PAYMENT_INSTRUCTIONS}
                          />
                        </div>
                      </div> :
                      <span style={(messageChooser[orderSummary.status] || {}).style}>
                        {t((messageChooser[orderSummary.status] || {}).message)}
                      </span>
                  }
                </Description>
                <Description className="m-b-1" title={t('paymentMethod')}>
                  <MethodForPayment
                    translator={t}
                    methodKey={orderSummary.paymentMethod}
                    alternativeMethodKey={
                      orderProcessing ? orderProcessing.paymentMethod :
                        PaymentMethodKeyReverse[PaymentMethod.PAYMENT_METHOD_NONE]
                    }
                  />
                </Description>
                <Description className="m-b-1" title={t('packagePlanDetail')}>
                  <TableDetail>
                    {
                      orderSummary.subscriptionsList.map((e, index) => {
                        return (
                          <React.Fragment key={index}>
                            <DetailItem title={t('packagePlanName')} value={e.planId}/>
                            {
                              e.end &&
                              <DetailItem title={t('expiryDate')} value={t('toDate', getDateProperty(e.end.seconds))}/>
                            }
                          </React.Fragment>
                        )
                      })
                    }
                    <DetailItem title={t('totalPayment')} value={toVND(orderSummary.amount)}/>
                  </TableDetail>
                </Description>
                <Description className="m-b-1" title={t('payerDetail')}>
                  <TableDetail>
                    <DetailItem title={t('payerName')} value={user.name}/>
                    <DetailItem title={t('payerPhoneNumber')} value={user.phone}/>
                  </TableDetail>
                </Description>
              </>
            }
          </div>
        </Paper>
      </StepRenderer>
    </PaymentLayout>
  );
};

export default Summary;
